import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { StepData, Block } from "@/types";
import Icon from "@/components/Icon";

interface Props {
  step: StepData;
}

/** Panneau de consigne (colonne gauche). La consigne est une suite de blocs
 *  ordonnés librement par l'enseignant : texte (Markdown), indice (repliable),
 *  image (affichée en ligne), fichier (lien). Les indices et images apparaissent
 *  donc là où ils ont été placés, plus dans des sections fixes. */
export default function InstructionPanel({ step }: Props) {
  let hintNo = 0; // numérotation des indices dans l'ordre d'apparition
  return (
    <div style={styles.panel}>
      <div style={styles.content}>
        <h2 style={styles.stepHeading}>{step.title}</h2>
        {step.blocks.map((b) => {
          if (b.kind === "hint") hintNo += 1;
          return <BlockView key={b.id} block={b} hintIndex={hintNo} />;
        })}
      </div>
    </div>
  );
}

function BlockView({ block, hintIndex }: { block: Block; hintIndex: number }) {
  switch (block.kind) {
    case "text":
      return (
        <div style={styles.markdown}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.text ?? ""}</ReactMarkdown>
        </div>
      );
    case "hint":
      return <HintItem content={block.text ?? ""} index={hintIndex} />;
    case "image":
      return <ImageBlock block={block} />;
    case "file":
      return <FileBlock block={block} />;
    default:
      return null;
  }
}

function HintItem({ content, index }: { content: string; index: number }) {
  // Aide repliable : masquée par défaut, dépliable et repliable à volonté.
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.hintItem}>
      <button style={styles.hintHeader} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Icon name={open ? "expanded" : "collapsed"} size={14} style={{ color: "var(--text-muted)" }} />
        Indice {index}
      </button>
      {open && (
        <div style={styles.hintContent}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function ImageBlock({ block }: { block: Block }) {
  if (!block.attachment) return null;
  return (
    <figure style={styles.figure}>
      <img
        src={`/api/exercises/attachments/${block.attachment.id}`}
        alt={block.alt ?? block.caption ?? ""}
        style={styles.image}
      />
      {block.caption && <figcaption style={styles.caption}>{block.caption}</figcaption>}
    </figure>
  );
}

function FileBlock({ block }: { block: Block }) {
  if (!block.attachment) return null;
  return (
    <a
      href={`/api/exercises/attachments/${block.attachment.id}?download=1`}
      target="_blank"
      rel="noreferrer"
      style={styles.fileLink}
    >
      <Icon name="file" size={15} />
      {block.caption || block.attachment.filename}
    </a>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: "var(--bg-base)",
    borderRight: "1px solid var(--border-soft)",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "24px 24px 32px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  stepHeading: {
    flexShrink: 0,
    color: "var(--text)",
    margin: "0 0 2px",
    fontSize: 19,
    fontWeight: 600,
    letterSpacing: -0.2,
  },
  markdown: {
    flexShrink: 0,
    color: "var(--text)",
    fontSize: 15,
    lineHeight: 1.7,
  },
  hintItem: {
    // flexShrink:0 essentiel : sans ça, un bloc image grand (panneau large) fait
    // dépasser la hauteur et flexbox compresse les indices en une fine barre.
    flexShrink: 0,
    background: "var(--bg-panel)",
    borderRadius: "var(--radius)",
    color: "var(--text)",
    overflow: "hidden",
  },
  hintHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    color: "var(--text)",
    padding: "12px 14px",
    fontSize: 14,
    fontWeight: 600,
  },
  hintContent: {
    padding: "0 14px 14px",
    fontSize: 14,
    lineHeight: 1.7,
  },
  figure: {
    flexShrink: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  image: {
    maxWidth: "100%",
    maxHeight: 460,
    width: "auto",
    height: "auto",
    objectFit: "contain",
    alignSelf: "flex-start",
    borderRadius: "var(--radius)",
    border: "1px solid var(--border-soft)",
  },
  caption: {
    fontSize: 12,
    color: "var(--text-muted)",
    fontStyle: "italic",
  },
  fileLink: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "var(--blue)",
    fontSize: 14,
    alignSelf: "flex-start",
  },
};
